import { DomainError, TransportError } from "@econ-v1/domain";
import { sha256Hex } from "../cache/sha256.js";
import { createQrMatrix } from "../encoding/qr-code.js";
import { ClientTransportDiscovery } from "./client-transport-discovery.js";
export class DeviceSessionRevokedError extends Error {
    constructor() {
        super("This client device was revoked");
        this.name = "DeviceSessionRevokedError";
    }
}
export class DeviceSessionClient {
    #baseUrl;
    #fetch;
    #identity;
    #transportDiscovery;
    #session;
    #stunUrls = [];
    constructor(options) {
        this.#baseUrl = new URL(options.baseUrl);
        this.#fetch = options.fetch ?? globalThis.fetch.bind(globalThis);
        this.#identity = options.identity;
        this.#transportDiscovery = new ClientTransportDiscovery({ fetch: this.#fetch });
    }
    currentSession() {
        return this.#session;
    }
    /**
     * The most recently discovered qualified STUN server URLs (ticket
     * #1865/#1866 review "FIX 4"), refreshed on every successful
     * `#bootstrapTransportMetadata`/`refreshTransportCandidates` round trip.
     * Empty until the first transport discovery round trip completes, and
     * stays empty forever against a node build that doesn't advertise
     * `stun_urls` — the same host-candidates-only fallback as before this
     * field existed. Read synchronously (never awaited) by the WAN WebRTC
     * promotion factory (`system/pwa/src/kernel-runtime.ts`) each time it
     * attempts a promotion, so a promotion attempt always uses the latest
     * known list rather than a value captured once at kernel construction
     * time (before any transport discovery has run).
     */
    currentStunUrls() {
        return this.#stunUrls;
    }
    defaultDeviceName() {
        const browserNavigator = navigator;
        const platform = browserNavigator.userAgentData?.platform ?? browserNavigator.platform;
        return platform.trim() === "" ? "Client Node browser" : `Client Node on ${platform}`;
    }
    async beginPairing(options) {
        const identity = await this.#identity.generate();
        const purpose = options.purpose ?? "pairing";
        const challenge = await this.#issueChallenge(identity, purpose, options.ownerPublicKey);
        if (purpose === "migration") {
            return {
                awaitingApproval: true,
                challenge,
                migrationVerified: false,
                status: "awaiting_migration_signature",
            };
        }
        return await this.#submitPairing(identity, challenge, options, undefined);
    }
    async completeMigration(challenge, options) {
        if (challenge.purpose !== "migration")
            throw new TypeError("Expected a migration challenge");
        const identity = await this.#identity.generate();
        return await this.#submitPairing(identity, challenge, options, {
            owner_public_key: options.ownerPublicKey,
            signature: options.ownerSignature,
        });
    }
    async establishSession(deviceName, deviceType = "pwa") {
        const identity = await this.#identity.generate();
        const challenge = await this.#issueChallenge(identity, "session");
        const signature = await this.#sign(identity, challenge.nonce);
        const data = await this.#request("/api/v2/client-devices/session", {
            body: {
                challenge_id: challenge.challengeId,
                device_name: deviceName,
                device_type: deviceType,
                identity: identityWire(identity),
                signature,
            },
            method: "POST",
        });
        const session = {
            delegationCredential: data.delegation_credential,
            delegationExpiresAt: data.delegation_expires_at,
            deviceId: data.device_id,
            scopes: [...data.scopes],
            sessionExpiresAt: data.session_expires_at,
            sessionToken: data.session_token,
        };
        await this.#identity.storeDelegation({
            credential: data.delegation_credential,
            expiresAt: data.delegation_expires_at,
            issuedAt: data.delegation_issued_at,
            revocationId: data.revocation_id,
            scopes: data.scopes,
        });
        this.#session = session;
        await this.#bootstrapTransportMetadata(session).catch(() => undefined);
        return session;
    }
    async #bootstrapTransportMetadata(session) {
        const transport = await this.#transportDiscovery.establishTrust(this.#baseUrl.origin, sessionHeaders(session));
        this.#stunUrls = transport.stunUrls;
        const current = await this.#identity.loadConnectionMetadata();
        await this.#identity.storeConnectionMetadata({
            directAddresses: transport.lan.map((candidate) => candidate.address),
            homeNodeId: transport.homeNodeId,
            ...(current?.lastConnectedAt === undefined ? {} : { lastConnectedAt: current.lastConnectedAt }),
            ...(current?.relayUrl === undefined ? {} : { relayUrl: current.relayUrl }),
            wanOrigin: transport.wan.address,
        });
    }
    async refreshTransportCandidates(signal) {
        const session = this.#requireSession();
        const current = await this.#identity.loadConnectionMetadata();
        if (current?.homeNodeId === undefined || current.homeNodeId.trim() === "") {
            throw new TransportError({ code: "offline", message: "Home node transport identity is not established" });
        }
        const transport = await this.#transportDiscovery.refresh(this.#baseUrl.origin, current.homeNodeId, sessionHeaders(session), signal);
        throwIfAborted(signal);
        this.#stunUrls = transport.stunUrls;
        await this.#identity.storeConnectionMetadata({
            directAddresses: transport.lan.map((candidate) => candidate.address),
            homeNodeId: current.homeNodeId,
            ...(current.lastConnectedAt === undefined ? {} : { lastConnectedAt: current.lastConnectedAt }),
            ...(current.relayUrl === undefined ? {} : { relayUrl: current.relayUrl }),
            wanOrigin: transport.wan.address,
        });
        // Deliberately NOT `return transport;`: `transport` also carries `stunUrls` (cached
        // above, on `this.#stunUrls`/`currentStunUrls()`) and an optional `webtransport` — this
        // method's return shape has always been exactly `{lan, wan}`, and callers (including
        // this class's own test suite) assert that exact shape.
        return { lan: transport.lan, wan: transport.wan };
    }
    async listDevices(includeRevoked = false) {
        const session = this.#requireSession();
        const data = await this.#request(`/api/v2/client-devices?include_revoked=${String(includeRevoked)}`, { delegation: session.delegationCredential, method: "GET", token: session.sessionToken });
        return data.map((device) => ({
            deviceId: device.device_id,
            deviceName: device.device_name,
            deviceType: device.device_type,
            id: device.id,
            isCurrent: device.device_id === session.deviceId,
            publicKeyMultibase: device.public_key_multibase,
            status: device.status,
        }));
    }
    async revokeDevice(id) {
        const session = this.#requireSession();
        const target = (await this.listDevices(true)).find((device) => device.id === id);
        await this.#request(`/api/v2/client-devices/${encodeURIComponent(id)}/revoke`, {
            method: "POST",
            delegation: session.delegationCredential,
            token: session.sessionToken,
        });
        if (target?.deviceId === session.deviceId)
            await this.#enterRevokedState();
    }
    async renameDevice(id, deviceName) {
        const session = this.#requireSession();
        const target = (await this.listDevices(true)).find((device) => device.id === id);
        if (target === undefined)
            throw new DomainError({ code: "not_found", message: "Client device was not found" });
        await this.#request(`/api/v2/devices/${encodeURIComponent(target.deviceId)}/name`, {
            body: { name: deviceName },
            delegation: session.delegationCredential,
            method: "PUT",
            token: session.sessionToken,
        });
    }
    async recoverAfterRevocation() {
        this.#session = undefined;
        await this.#identity.recoverAfterRevocation();
    }
    async #issueChallenge(identity, purpose, ownerPublicKey) {
        const body = {
            identity: identityWire(identity),
            purpose,
        };
        if (ownerPublicKey !== undefined)
            body.owner_public_key = ownerPublicKey;
        const data = await this.#request("/api/v2/client-devices/challenge", {
            body,
            method: "POST",
        });
        return {
            challengeId: data.challenge_id,
            expiresAt: data.expires_at,
            nonce: data.nonce,
            purpose: data.purpose,
            qrPayload: data.qr_payload,
            qrModules: createQrMatrix(data.qr_payload),
            shortCode: data.short_code,
        };
    }
    async #submitPairing(identity, challenge, options, migrationProof) {
        const signature = await this.#sign(identity, challenge.nonce);
        const body = {
            challenge_id: challenge.challengeId,
            device_name: options.deviceName,
            device_type: options.deviceType ?? "pwa",
            identity: identityWire(identity),
            signature,
        };
        if (migrationProof !== undefined)
            body.migration_proof = migrationProof;
        const data = await this.#request("/api/v2/client-devices/pair", {
            body,
            method: "POST",
        });
        const approval = data.operation_hash === undefined
            ? undefined
            : await approvalQrCode(this.#baseUrl, data.operation_hash);
        return {
            ...(approval === undefined ? {} : { approval }),
            awaitingApproval: data.awaiting_approval,
            challenge,
            ...(data.device_id === undefined ? {} : { deviceId: data.device_id }),
            migrationVerified: data.migration_verified,
            ...(data.operation_hash === undefined ? {} : { operationHash: data.operation_hash }),
            status: data.status,
        };
    }
    async #sign(identity, nonce) {
        const signature = await this.#identity.sign(identity.keyHandle, new TextEncoder().encode(nonce));
        return bytesToBase64Url(signature);
    }
    #requireSession() {
        if (this.#session === undefined) {
            throw new DomainError({ code: "invalid_request", message: "Client device session is not established" });
        }
        return this.#session;
    }
    async #enterRevokedState() {
        this.#session = undefined;
        await this.#identity.clearDelegation();
        await this.#identity.clear();
        throw new DeviceSessionRevokedError();
    }
    async #request(path, options) {
        const headers = new Headers({ accept: "application/json" });
        if (options.body !== undefined)
            headers.set("content-type", "application/json");
        if (options.token !== undefined)
            headers.set("authorization", `Bearer ${options.token}`);
        if (options.delegation !== undefined) {
            headers.set("x-client-device-delegation", options.delegation);
        }
        let response;
        try {
            response = await this.#fetch(new Request(new URL(path, this.#baseUrl), {
                ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
                headers,
                method: options.method,
            }));
        }
        catch {
            throw new TransportError({
                code: "offline",
                message: "Couldn't reach your node. Make sure this device is on the same Wi‑Fi network as your node, then try again.",
            });
        }
        const envelope = await readEnvelope(response);
        if (!response.ok || envelope.success !== true) {
            if (response.status === 403 && envelopeMessage(envelope).toLowerCase().includes("revoked")) {
                await this.#enterRevokedState();
            }
            throw new TransportError({ code: "unavailable", message: envelopeMessage(envelope) });
        }
        return envelope.data;
    }
}
function throwIfAborted(signal) {
    if (signal?.aborted === true)
        throw new DOMException("Transport candidate refresh aborted", "AbortError");
}
async function approvalQrCode(baseUrl, operationHash) {
    const approvalUrl = new URL("/devices", baseUrl);
    approvalUrl.searchParams.set("approve", operationHash);
    const qrPayload = approvalUrl.toString();
    const digest = await sha256Hex(new TextEncoder().encode(operationHash));
    const shortCode = (Number.parseInt(digest.slice(0, 12), 16) % 1_000_000)
        .toString()
        .padStart(6, "0");
    return { qrPayload, qrModules: createQrMatrix(qrPayload), shortCode };
}
function identityWire(identity) {
    return {
        device_did: identity.did,
        iroh_node_id: identity.nodeId,
        public_key_multibase: identity.publicKeyMultibase,
    };
}
function sessionHeaders(session) {
    return {
        authorization: `Bearer ${session.sessionToken}`,
        "x-client-device-delegation": session.delegationCredential,
    };
}
function bytesToBase64Url(bytes) {
    let binary = "";
    for (const byte of bytes)
        binary += String.fromCharCode(byte);
    return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/u, "");
}
async function readEnvelope(response) {
    try {
        const value = await response.json();
        return isRecord(value) ? value : {};
    }
    catch {
        return {};
    }
}
function envelopeMessage(envelope) {
    if (typeof envelope.error === "string")
        return envelope.error;
    if (isRecord(envelope.error) && typeof envelope.error.message === "string")
        return envelope.error.message;
    return "Home node rejected the client device request";
}
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
//# sourceMappingURL=device-session-client.js.map